import { Typography, IconButton } from '@app/components';
import { Tooltip } from '@mui/material';
import { clsxm } from '@app/styles/clsxm';
import { CustomerTimelineEntryOutDto, ETimelineEntryKind } from '@app/swagger-types';
import { format } from 'date-fns';
import { FORMAT } from '@app/constants/formats';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReplayIcon from '@mui/icons-material/Replay';
import CancelIcon from '@mui/icons-material/Cancel';
import { ReasonPromptDialog } from './ReasonPromptDialog';

const KIND_STYLE: Record<ETimelineEntryKind, string> = {
  [ETimelineEntryKind.SENT]: 'border-accept-medium bg-accept-light',
  [ETimelineEntryKind.SCHEDULED]: 'border-[#c7d2fe] bg-[#e8eaff]',
  [ETimelineEntryKind.SKIPPED]: 'border-gray-200 bg-gray-50',
  [ETimelineEntryKind.DROPPED]: 'border-[#fde2e2] bg-[#fde2e2]/40',
};

// admin-emailbeheer blok A stap 5, opdracht §5 "Tijdlijn". One row per merged
// email_send_log/email_report/email_schedule entry - see CustomerTimelineEntryOutDto's own
// backend javadoc for exactly what each kind means and where recordId/recordType point.
export const TimelineEntryCard: React.FC<{
  entry: CustomerTimelineEntryOutDto;
  onView: () => void;
  onResend: (reason: string) => void;
  onCancel: (reason: string) => void;
  resendLoading?: boolean;
  cancelLoading?: boolean;
}> = ({ entry, onView, onResend, onCancel, resendLoading, cancelLoading }) => {
  const canActOnContent = entry.contentAvailable && entry.recordType && entry.recordId;

  return (
    <div
      className={clsxm(
        'flex items-start justify-between gap-3 rounded-lg border p-3',
        entry.kind && KIND_STYLE[entry.kind]
      )}
    >
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded bg-white px-1.5 py-0.5 text-xs font-bold uppercase text-gray-600">{entry.kind}</span>
          <Typography variant="s" className="font-bold">
            {entry.mailKey}
          </Typography>
          {entry.category && <span className="text-xs text-gray-400">{entry.category}</span>}
          {entry.locale && <span className="text-xs text-gray-400">{entry.locale}</span>}
          {entry.seriesNo != null && entry.sequenceNo != null && (
            <span className="text-xs text-gray-400">
              series {entry.seriesNo}, mail {entry.sequenceNo} of {entry.sequenceMax}
            </span>
          )}
        </div>

        <Typography variant="xs" className="text-gray-500">
          {entry.at ? format(new Date(entry.at), FORMAT.FULL_TIME) : '-'}
        </Typography>

        {entry.kind === ETimelineEntryKind.SCHEDULED && (
          <Typography variant="xs" className="mt-1 italic text-sky-700">
            Conditions are checked at send time - this is not a promise it will actually go out.
          </Typography>
        )}

        {entry.kind === ETimelineEntryKind.SKIPPED && (
          <Typography variant="xs" className="mt-1 text-gray-600">
            skip_reason={entry.skipReason}
          </Typography>
        )}

        {entry.kind === ETimelineEntryKind.DROPPED && (
          <Typography variant="xs" className="mt-1 text-red-700">
            Held back by SendGrid: {entry.droppedReason}
          </Typography>
        )}

        {!!entry.events?.length && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {entry.events.map((event, i) => (
              <Tooltip key={i} title={event.occurredAt ? format(new Date(event.occurredAt), FORMAT.FULL_TIME) : ''}>
                <span className="rounded bg-white px-1.5 py-0.5 text-xs">
                  {event.eventType}
                  {event.reason && <span className="text-gray-400"> ({event.reason})</span>}
                </span>
              </Tooltip>
            ))}
          </div>
        )}

        {entry.contentAvailable === false &&
          (entry.kind === ETimelineEntryKind.SENT || entry.kind === ETimelineEntryKind.DROPPED) && (
            <Typography variant="xs" className="mt-1 text-gray-400">
              Sent before content capture existed - can&apos;t be viewed or resent.
            </Typography>
          )}
      </div>

      <div className="flex shrink-0 flex-col gap-1">
        {canActOnContent && (
          <>
            <Tooltip title="View rendered mail">
              <IconButton size="small" onClick={onView}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <ReasonPromptDialog
              title="Resend this mail?"
              text="This respects suppression and unsubscribes - it will not send if either applies."
              actionLabel="Resend"
              loading={resendLoading}
              onConfirm={onResend}
              trigger={
                <Tooltip title="Resend">
                  <IconButton size="small">
                    <ReplayIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
          </>
        )}
        {entry.kind === ETimelineEntryKind.SCHEDULED && (
          <ReasonPromptDialog
            title="Cancel this scheduled mail?"
            actionLabel="Cancel mail"
            loading={cancelLoading}
            onConfirm={onCancel}
            trigger={
              <Tooltip title="Cancel">
                <IconButton size="small">
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          />
        )}
      </div>
    </div>
  );
};
